import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Backdrop, ClickAwayListener } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconCloseThin } from 'src/assets/icons/CloseThin';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ToneOfVoiceList } from 'src/components/ToneOfVoiceList';
import { GENERATION_PANEL_MENU_TYPE } from 'src/const';
import { useListenToGenerationPanelMenuOpen } from 'src/lib/hooks';
import { useSetupMenuFloating } from 'src/lib/hooks/useSetupMenuFloating';
import pubsub, { PUBSUB } from 'src/lib/pubsub';

import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  referenceElement: PropTypes.instanceOf(Element),
  isMenuOpen: PropTypes.bool,
  onClose: PropTypes.func,
  isInfinityMode: PropTypes.bool,
  ToneOfVoiceListProps: PropTypes.shape(ToneOfVoiceList.propTypes).isRequired,
};
const defaultProps = {
  className: null,
  referenceElement: null,
  isMenuOpen: false,
  onClose: null,
  isInfinityMode: false,
};

export const ToneOfVoiceMenu = ({
  className,
  referenceElement,
  isMenuOpen,
  onClose,
  isInfinityMode,
  ToneOfVoiceListProps,
}) => {
  const styles = useStyles();

  const [popperElement, setPopperElement] = useState(null);
  const { floatingStyles } = useSetupMenuFloating({
    popperElement,
    referenceElement,
    isInfinityMode,
  });

  useListenToGenerationPanelMenuOpen(GENERATION_PANEL_MENU_TYPE.toneOfVoice, onClose);

  useEffect(() => {
    if (isMenuOpen) {
      pubsub.publish(PUBSUB.OPEN_GENERATION_PANEL_MENU, {
        openedMenuType: GENERATION_PANEL_MENU_TYPE.toneOfVoice,
      });
    }
  }, [isMenuOpen]);

  if (!isMenuOpen) {
    return null;
  }

  return createPortal(
    <>
      <Backdrop
        className={styles.backdrop}
        open={isMenuOpen}
        onClick={onClose}
        invisible
      />
      <ClickAwayListener onClickAway={onClose}>
        <div
          className={cx(className, styles.root, {
            [styles.isInfinityMode]: isInfinityMode,
          })}
          ref={setPopperElement}
          style={floatingStyles}
        >
          <div className={styles.header}>
            <CustomTypography variant={TYPOGRAPHY_VARIANT.medium12}>Tone of voice</CustomTypography>
            <IconCloseThin
              className={styles.buttonClose}
              onClick={onClose}
            />
          </div>

          <ToneOfVoiceList
            className={styles.toneOfVoiceList}
            isInfinityMode={isInfinityMode}
            {...ToneOfVoiceListProps}
          />
        </div>
      </ClickAwayListener>
    </>,
    document.body
  );
};

ToneOfVoiceMenu.displayName = 'ToneOfVoiceMenu';
ToneOfVoiceMenu.propTypes = propTypes;
ToneOfVoiceMenu.defaultProps = defaultProps;
